<ng-container *ngIf="display$ | async as vm">
  <!-- The row-detail request is in flight. hs-loader is deliberately not used here — its host sits
       at opacity: 0 until an `active` class is added on top of `[loading]`, so `[loading]` alone
       renders an invisible overlay (see workbench-grid.component.html for the same note, and why
       its nfLoader directive is not an option for an ag-Grid host either). -->
  <div
    *ngIf="vm.loading"
    class="detail-loading"
    data-cy="detail-loading"
    role="status"
    aria-live="polite"
  >
    <span class="spinner-border" aria-hidden="true"></span>
    <span class="detail-loading-label">Loading...</span>
  </div>

  <!-- Nothing selected, or more than one row selected: the shared empty-state cards. -->
  <hs-panel-card
    *ngIf="vm.emptyState as emptyState"
    [header]="emptyState.header"
  >
    <p>{{ emptyState.body }}</p>
  </hs-panel-card>

  <!-- The anchor is a Clearwater row. There is no row-detail endpoint for CW, so no request was
       issued — see the TODO in position-detail.component.ts. -->
  <div
    *ngIf="vm.unavailable"
    class="detail-status detail-status--info"
    data-cy="detail-unavailable"
  >
    <i class="mdi mdi-information-outline" aria-hidden="true"></i>
    Position detail is not available for Clearwater rows.
  </div>

  <!-- The request failed. An inline strip matching the grid's own wb-columns-error, never a modal
       or a thrown error: one failed fetch must not break the tab. -->
  <div
    *ngIf="vm.error"
    class="detail-status detail-status--error"
    data-cy="detail-error"
    role="alert"
  >
    <i class="mdi mdi-alert-circle" aria-hidden="true"></i>
    Couldn't load the position detail.
  </div>

  <!-- A legitimate 200 that carried no fields for this row. -->
  <div
    *ngIf="vm.noData"
    class="detail-status detail-status--info"
    data-cy="detail-no-data"
  >
    <i class="mdi mdi-information-outline" aria-hidden="true"></i>
    No detail is available for this position.
  </div>

  <!-- A row is loaded and has fields: toolbar, table and footer. The toolbar collapses entirely
       when there is nothing loaded, rather than offering filters over an empty table. -->
  <div class="position-detail" *ngIf="vm.loaded">
    <div class="detail-toolbar">
      <span class="detail-toolbar__label">COLUMN GROUPS</span>

      <div ngbDropdown class="detail-groups" [autoClose]="'outside'">
        <button
          type="button"
          ngbDropdownToggle
          class="detail-groups__toggle"
          data-cy="detail-groups-toggle"
          aria-label="Column groups"
        >
          <span>{{ vm.groupsLabel }}</span>
          <i class="mdi mdi-chevron-down" aria-hidden="true"></i>
        </button>
        <div ngbDropdownMenu class="detail-groups__menu">
          <label class="detail-groups__item">
            <input
              type="checkbox"
              [checked]="vm.allGroupsShown"
              (change)="toggleAllGroups(vm.groups)"
            />
            Select All
          </label>
          <div class="dropdown-divider"></div>
          <!-- One checkbox per group present in the response, in response order. -->
          <label
            class="detail-groups__item"
            *ngFor="let group of vm.groups; trackBy: trackByGroup"
          >
            <input
              type="checkbox"
              [checked]="isGroupShown(group)"
              (change)="toggleGroup(group)"
            />
            {{ group }}
          </label>
        </div>
      </div>

      <div class="detail-toolbar__divider" aria-hidden="true"></div>

      <label class="detail-toolbar__checkbox">
        <input
          type="checkbox"
          data-cy="detail-hide-empty"
          [checked]="vm.hideEmpty"
          (change)="onHideEmptyChange($event)"
        />
        Hide empty fields
      </label>

      <input
        type="text"
        class="detail-toolbar__search"
        data-cy="detail-search"
        placeholder="Search Table..."
        aria-label="Search Table"
        [value]="vm.search"
        (input)="onSearchChange($event)"
      />

      <!-- The design's kebab, at the far right of the toolbar. One action, with the same
           "Export to Excel" label the two grids above use; this one writes a real .xlsx — see the
           note on exportToExcel(). Disabled when the filters leave nothing on screen (Fields: 0);
           [attr.disabled] backs up [disabled] in case ngbDropdownItem's own disabled input
           intercepts the property binding. -->
      <div ngbDropdown placement="bottom-right" class="detail-kebab">
        <button
          ngbDropdownToggle
          nf-icon-button
          class="detail-kebab__toggle"
          data-cy="detail-kebab"
          icon="dots-vertical"
          title="More options"
        >
          More options
        </button>
        <div ngbDropdownMenu class="dropdown-menu-right">
          <button
            type="button"
            ngbDropdownItem
            data-cy="detail-export"
            [disabled]="vm.shownCount === 0"
            [attr.disabled]="vm.shownCount === 0 ? '' : null"
            (click)="exportToExcel()"
          >
            Export to Excel
          </button>
        </div>
      </div>
    </div>

    <nf-table-layout
      [hideTitle]="true"
      [isSectionTitle]="true"
      [gridOptions]="gridOptions"
      [modules]="gridModules"
      [data]="vm.rows"
      hideSearch
      hideClearAll
      [autoResizeGrid]="false"
    >
    </nf-table-layout>
  </div>
</ng-container>
